/**
 * Item page, once the item is in a Plan of Record in force (owner ruling
 * 2026-10-15): the plan's rank of record at the top, Planning & delivery right
 * after Overview (the page still opens on Overview, owner 2026-10-16), and the
 * fields the plan depends on no longer edited in place. "How it was
 * ranked" opens the Prioritization tab, where the item's rankings are; with more
 * than one plan in force the band also picks the plan.
 *
 * Until 2026-10-16 an Action / How it was ranked switch turned all of this off
 * and showed the ordinary page — unlocking the plan's fields — which is not what
 * its name said (owner, 2026-10-16). The switch is gone.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { setAmendingPlan } from '@/lib/itemCycleContext';
import { LABEL_MONO } from '@/components/mono/primitives';
import { StructureVersionFinePrint } from '@/components/hierarchy/StructureVersionFinePrint';
import { ActionLinkView, type RowEditor } from './ItemDetailThirdPassView';

export interface ActionPlan {
  planCycleId: string;
  name: string;
  href: string;
  /** The cycle the plan was built from; null when the database ties it to none. */
  cycleId: string | null;
  rank: number | null;
  aboveTheLine: boolean | null;
  authorizedAt: string | null;
  authorizedBy: string | null;
}

const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null);

export function PlanActionBand({
  plans,
  planId,
  onShowRanking,
  onPlan,
}: {
  plans: ActionPlan[];
  planId: string;
  /** Opens the item's Prioritization tab. */
  onShowRanking: () => void;
  onPlan: (id: string) => void;
}) {
  const plan = plans.find((p) => p.planCycleId === planId) ?? plans[0];
  if (!plan) return null;
  // The chosen plan is raised above its neighbors so all four of its edges keep
  // the darker stroke.
  const seg = (on: boolean) =>
    `border px-2.5 py-1 text-xs ${on ? 'relative z-10 border-[var(--slate)] bg-[var(--slate-dim)] font-medium text-[var(--text-pri)]' : 'border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'}`;

  return (
    <div className="mt-4 border border-[var(--rule)] bg-[var(--surface)] px-4 py-3" data-testid="plan-action-band">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={LABEL_MONO}>In the plan of record</div>
          <p className="mt-1 text-sm text-[var(--text-pri)]">
            <strong>{plan.rank != null ? `#${plan.rank}` : 'Ranked'}</strong> in{' '}
            <Link to={plan.href} className="underline">
              {plan.name}
            </Link>
            {plan.aboveTheLine === false ? ' · below the line' : ''}
            {plan.authorizedAt ? ` · authorized ${day(plan.authorizedAt)}` : ''}
            {plan.authorizedBy ? ` by ${plan.authorizedBy}` : ''}
          </p>
          <StructureVersionFinePrint table="plan_cycles" id={plan.planCycleId} mode="started" className="mt-0.5" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {plans.length > 1 && (
            <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="Which plan">
              {plans.map((p) => (
                <button key={p.planCycleId} type="button" role="radio" aria-checked={p.planCycleId === plan.planCycleId} onClick={() => onPlan(p.planCycleId)} className={seg(p.planCycleId === plan.planCycleId)}>
                  {p.name}
                </button>
              ))}
            </div>
          )}
          <ActionLinkView action={{ label: 'How it was ranked', onClick: onShowRanking }} />
        </div>
      </div>
    </div>
  );
}

/** The plan whose lock this page meets, and the ways the person may still change a field it holds. */
export interface PlanLock {
  name: string;
  href: string;
  planCycleId: string;
  /** The person may amend this plan (938's rule, read by get_item_cycle_standing). */
  mayAmend: boolean;
  /** Cycles still ranking the item, linked to this item in that cycle. */
  rankingCycles: Array<{ name: string; href: string }>;
}

/** Writes made while this is on screen amend the plan; the server checks the person may (migration 1019). */
function AmendingPlan({ planCycleId, children }: { planCycleId: string; children: ReactNode }) {
  useEffect(() => {
    setAmendingPlan(planCycleId);
    return () => setAmendingPlan(null);
  }, [planCycleId]);
  return <>{children}</>;
}

/**
 * In Action, a field the plan holds opens this instead of an editor. It says what
 * the person can do: amend the plan here, if they may; change it in a cycle still
 * ranking the item; or who holds the amendment. The plan keeps a change out until
 * it is authorized again (owner ruling, 2026-10-30).
 */
export function PlanLockedNote({ label, plan, editor, close }: { label: string; plan: PlanLock; editor: RowEditor; close: () => void }) {
  const [amending, setAmending] = useState(false);
  if (amending) {
    return (
      <AmendingPlan planCycleId={plan.planCycleId}>
        <p className="mb-2 text-sm text-[var(--text-sec)]" data-testid="plan-amending-note">
          Amending <strong className="text-[var(--text-pri)]">{plan.name}</strong>. Saving makes a new version of the item; the plan keeps the version it holds until it is
          authorized again.
        </p>
        {editor(close)}
      </AmendingPlan>
    );
  }
  return (
    <div className="space-y-2 text-sm text-[var(--text-sec)]" data-testid="plan-locked-note">
      <p>
        {label} is part of{' '}
        <Link to={plan.href} className="font-medium text-[var(--text-pri)] underline">
          {plan.name}
        </Link>
        , which holds the item as it was authorized.
      </p>
      {plan.mayAmend && (
        <div>
          <ActionLinkView action={{ label: `Change it as an amendment to ${plan.name}`, onClick: () => setAmending(true) }} />
        </div>
      )}
      {plan.rankingCycles.length > 0 && (
        <p>
          {plan.mayAmend ? 'Or change it' : 'It can be changed'} for{' '}
          {plan.rankingCycles.map((c, i) => (
            <span key={c.href}>
              {i > 0 ? (i === plan.rankingCycles.length - 1 ? ' or ' : ', ') : ''}
              <Link to={c.href} className="underline">
                {c.name}
              </Link>
            </span>
          ))}
          , which {plan.rankingCycles.length === 1 ? 'is' : 'are'} still ranking it; {plan.name} keeps that change out too.
        </p>
      )}
      {!plan.mayAmend && (
        <p>To change it for {plan.name}, the plan&rsquo;s owner, its executive sponsor or a workspace admin amends the plan.</p>
      )}
      <button type="button" className="text-xs underline text-[var(--text-ter)]" onClick={close}>
        Close
      </button>
    </div>
  );
}
